import type { FC } from 'react';
import { useCallback } from 'react';
import type { Control } from 'react-hook-form';

import { useAppDispatch, useAppSelector } from 'features/app/hooks';
import { handleError } from 'features/notification/store';
import { TeamingBaseRequestForm } from 'features/teaming/teaming-base/components';
import type { TeamRequestFormOptions } from 'features/teaming/teaming-base/interfaces';
import {
  requestRemoteTeaming,
  setTeamingReason,
  setTeamingStatus,
} from 'features/teaming/teaming-base/store';
import type { CustomSelectItem } from 'features/ui/elements';
import { voiceSessionIdSelector } from 'features/voice-session/store';
import { useVoiceMeetingSessionConnect } from 'features/voice-meeting/hooks';
import {
  TeamingReason,
  TeamingStatus,
} from 'features/teaming/teaming-base/enums';
import { sendAnalytics } from 'features/analytics/store';
import { LogLevel } from 'features/analytics/enums';
import { isZoomConferenceSelector } from 'features/call/call-conference/store';

interface TeamingRemoteRequestFormProps {
  control: Control<TeamRequestFormOptions>;
  isValid: boolean;
  isLoading: boolean;
  getFormValues: () => TeamRequestFormOptions;
  setLoading: (loading: boolean) => void;
  reasonOptions: CustomSelectItem[];
  locationOptions: CustomSelectItem[];
  closePopover: () => void;
}

export const TeamingRemoteRequestForm: FC<TeamingRemoteRequestFormProps> = ({
  control,
  isValid,
  isLoading,
  getFormValues,
  setLoading,
  reasonOptions,
  closePopover,
  locationOptions,
}) => {
  const dispatch = useAppDispatch();
  const voiceSessionId = useAppSelector(voiceSessionIdSelector);
  const isZoomConference = useAppSelector(isZoomConferenceSelector);
  const { startNewVoiceSessionOnlyAgent } = useVoiceMeetingSessionConnect();

  const getRequestOptions = useCallback(() => {
    const formOptions = getFormValues();
    return {
      reason: TeamingReason[formOptions.reason],
      gender: Number(formOptions.gender),
      language: formOptions.language,
      teamingType: formOptions.location,
      isRemote: true,
    };
  }, [getFormValues]);

  const handleRequestTeaming = useCallback(
    async () => {
      setLoading(true);
      try {
        dispatch(setTeamingStatus(TeamingStatus.REQUESTED));

        if (!voiceSessionId && !isZoomConference) {
          dispatch(
            sendAnalytics({
              Method: 'handleRequestTeaming',
              Level: LogLevel.INFO,
              Message: 'Adding voice session for remote teaming request',
            })
          );
          await startNewVoiceSessionOnlyAgent();
        }

        const options = getRequestOptions();
        dispatch(setTeamingReason(options.reason));
        await dispatch(requestRemoteTeaming(options)).unwrap();
      } catch (error) {
        dispatch(
          handleError({
            error,
            methodName: 'handleRequestTeaming',
          })
        );
        dispatch(setTeamingStatus(TeamingStatus.NONE));
      } finally {
        setLoading(false);
        closePopover();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [voiceSessionId, closePopover, getRequestOptions]
  );

  return (
    <TeamingBaseRequestForm
      onSubmitButtonClick={handleRequestTeaming}
      control={control}
      isLoading={isLoading}
      isValid={isValid}
      reasonOptions={reasonOptions}
      locationOptions={locationOptions}
      closePopover={closePopover}
    />
  );
};
